import { emitSessionExpired } from '../context/SessionExpiredContext';

const API_BASE_URL = 'https://beta.linkperksapp.com';

// Track if we've already emitted a session expired event to prevent duplicates
let hasEmittedSessionExpired = false;

// Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Reset the flag when user logs in again
export const resetSessionExpiredFlag = () => {
  hasEmittedSessionExpired = false;
};

// Refresh the access token using the stored refresh token
const refreshAccessToken = async (): Promise<string | null> => {
  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  
  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        console.log('No refresh token available');
        return null;
      }

      console.log('Attempting to refresh access token...');
      
      const response = await fetch(`${API_BASE_URL}/api/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        console.log('Refresh token request failed:', response.status);
        // Refresh token is invalid, clear tokens
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        return null;
      }

      const data = await response.json();
      const newAccessToken = data.access_token;
      
      // Store the new access token
      localStorage.setItem('auth_token', newAccessToken);
      
      // If a new refresh token is returned, store it too
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }

      console.log('Access token refreshed successfully');
      return newAccessToken;
    } catch (error) {
      console.error('Error refreshing access token:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Helper to check for 401 and emit session expired (only after refresh attempt fails)
const handleUnauthorized = (response: Response, isLoginRequest: boolean = false): void => {
  // Don't trigger session expired for login/register requests (those are expected to fail sometimes)
  if (!isLoginRequest && response.status === 401 && !hasEmittedSessionExpired) {
    hasEmittedSessionExpired = true;
    emitSessionExpired();
  }
};

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  is_business_admin?: boolean;
}

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in?: number;
}

export interface UserInfo {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
}

export interface AuthResponse {
  token: string;
  user: UserInfo;
}

export interface RegisteredUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  created_at?: string;
  status?: string;
}

// Location within a business
export interface BusinessLocationItem {
  id?: number;
  address: string;
  latitude: number;
  longitude: number;
}

// Business categories (must match mobile app categories)
export const BUSINESS_CATEGORIES = [
  // 'Network',
  'Services',
  'Auto Services',
  'Home Services',
  'Restaurants',
  'Health',
  'Retail',
  'Entertainment',
  'Fitness',
  'Ecommerce',
] as const;

export type BusinessCategory = typeof BUSINESS_CATEGORIES[number];

// Redemption mode drives the FE redemption flow
export type RedemptionMode = 'in_store' | 'webpage' | 'url_with_code';

// Business interfaces for Business Details
export interface BusinessData {
  name: string;
  description?: string;
  image?: string;
  cover_image?: string;
  category?: BusinessCategory;
  email_domain?: string | null; // Enforced company email domain at signup
  locations?: BusinessLocationItem[];
}

export interface Business extends BusinessData {
  id: number;
  created_at?: string;
  updated_at?: string;
}

// Aliases for backwards compatibility
export type LocationData = BusinessData;
export type Location = Business;

// Offering interfaces
export interface OfferingData {
  name: string;
  description: string;
  image?: string;
  type: string;
  expiration_date?: string;
  amount?: number;
  distance?: number;
  is_ecom?: boolean;
  code?: string;  // Promo code for e-commerce offerings
  redemption_mode?: RedemptionMode; // in_store | webpage | url_with_code
  website_url?: string | null; // Required for webpage / url_with_code modes
  is_single_use?: boolean; // When true, each user can redeem only once. Defaults to unlimited.
  location_ids?: number[];
}

// Location reference in offering response (API might return full objects)
export interface OfferingLocation {
  id: number;
  address?: string;
}

export interface Offering extends OfferingData {
  id: number;
  created_at?: string;
  updated_at?: string;
  // API might return locations as objects instead of just IDs
  locations?: OfferingLocation[];
}

class ApiService {
  private getHeaders(includeAuth: boolean = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  // Authenticated fetch with automatic token refresh on 401
  private async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    // Add authorization header
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    // Make the initial request
    let response = await fetch(url, { ...options, headers });

    // If we get a 401, try to refresh the token
    if (response.status === 401) {
      console.log('Received 401, attempting token refresh...');
      
      const newToken = await refreshAccessToken();
      
      if (newToken) {
        // Retry the request with the new token
        const retryHeaders = {
          'Content-Type': 'application/json',
          ...options.headers,
          Authorization: `Bearer ${newToken}`,
        };
        
        response = await fetch(url, { ...options, headers: retryHeaders });
        
        // If still 401 after refresh, emit session expired
        if (response.status === 401) {
          console.log('Still 401 after token refresh');
          handleUnauthorized(response);
        }
      } else {
        // Refresh failed, emit session expired
        handleUnauthorized(response);
      }
    }

    return response;
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Step 1: Login to get tokens
    const loginResponse = await fetch(`${API_BASE_URL}/api/login/`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(credentials),
    });

    if (!loginResponse.ok) {
      const error = await loginResponse.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(error.error || 'Login failed');
    }

    const tokenData: TokenResponse = await loginResponse.json();
    
    // Store tokens
    const token = tokenData.access_token;
    localStorage.setItem('auth_token', token);
    if (tokenData.refresh_token) {
      localStorage.setItem('refresh_token', tokenData.refresh_token);
    }
    
    // Step 2: Get user info including role
    const userInfoResponse = await fetch(`${API_BASE_URL}/api/user-info/`, {
      method: 'GET',
      headers: {
        ...this.getHeaders(),
        'Authorization': `Bearer ${token}`
      },
    });

    if (!userInfoResponse.ok) {
      throw new Error('Failed to fetch user information');
    }

    const userInfo: UserInfo = await userInfoResponse.json();

    return {
      token: token,
      user: userInfo
    };
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    // Step 1: Register to get tokens
    const registerResponse = await fetch(`${API_BASE_URL}/api/register/`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!registerResponse.ok) {
      const error = await registerResponse.json().catch(() => ({ error: 'Registration failed' }));
      throw new Error(error.error || 'Registration failed');
    }

    const tokenData: TokenResponse = await registerResponse.json();
    
    // Store tokens
    const token = tokenData.access_token;
    localStorage.setItem('auth_token', token);
    if (tokenData.refresh_token) {
      localStorage.setItem('refresh_token', tokenData.refresh_token);
    }
    
    // Step 2: Get user info including role
    const userInfoResponse = await fetch(`${API_BASE_URL}/api/user-info/`, {
      method: 'GET',
      headers: {
        ...this.getHeaders(),
        'Authorization': `Bearer ${token}`
      },
    });

    if (!userInfoResponse.ok) {
      throw new Error('Failed to fetch user information');
    }

    const userInfo: UserInfo = await userInfoResponse.json();

    return {
      token: token,
      user: userInfo
    };
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/api/logout`, {
        method: 'POST',
        headers: this.getHeaders(true),
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
    }
  }

  async verifyToken(): Promise<UserInfo> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/user-info/`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Token verification failed');
    }

    return response.json();
  }

  async getBusinesses(): Promise<Business[]> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/businesses`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch businesses');
    }

    const data = await response.json();
    
    // Handle various response formats
    if (Array.isArray(data)) {
      return data;
    }
    if (data.businesses && Array.isArray(data.businesses)) {
      return data.businesses;
    }
    if (data.results && Array.isArray(data.results)) {
      return data.results;
    }
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    console.log('Unexpected businesses response format:', data);
    return [];
  }

  // GET - Fetch all public businesses (includes businesses without locations)
  async getAllPublicBusinesses(): Promise<Business[]> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/businesses/`, {
      method: 'GET',
    });

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      throw new Error('Failed to fetch public businesses');
    }

    const data = await response.json();
    if (Array.isArray(data)) {
      return data;
    }
    if (data.businesses && Array.isArray(data.businesses)) {
      return data.businesses;
    }
    if (data.results && Array.isArray(data.results)) {
      return data.results;
    }
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  }

  // GET - Fetch all businesses using the linkperks nearby endpoint with a large radius
  async getAllBusinessesNearby(): Promise<LinkPerksBusinessResponse[]> {
    // Use Winnipeg, Manitoba coordinates with massive radius to get all businesses
    const latitude = 49.8951;
    const longitude = -97.1384;
    const distance = 50000000; // 50 million meters (50,000 km) - covers entire planet

    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/api/linkperks/businesses/?latitude=${latitude}&longitude=${longitude}&distance=${distance}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      throw new Error('Failed to fetch businesses');
    }

    const data = await response.json();
    
    // Handle various response formats
    if (Array.isArray(data)) {
      return data;
    }
    if (data.businesses && Array.isArray(data.businesses)) {
      return data.businesses;
    }
    if (data.results && Array.isArray(data.results)) {
      return data.results;
    }
    return [];
  }

  async getUsers(): Promise<RegisteredUser[]> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/users/`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    return response.json();
  }

  // Business Management APIs
  // GET - Fetch business details (returns single object or null/empty if none exists)
  async getBusiness(): Promise<Business | null> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/business-admin/business/`, {
      method: 'GET',
    });

    if (!response.ok) {
      // 404 means no business exists yet
      if (response.status === 404) {
        return null;
      }
      throw new Error('Failed to fetch business details');
    }

    const data = await response.json();
    // Handle if API returns array (take first) or single object
    if (Array.isArray(data)) {
      return data.length > 0 ? data[0] : null;
    }
    return data;
  }

  // POST - Create new business with metadata and locations
  async createBusiness(data: BusinessData): Promise<Business> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/business-admin/business/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create business' }));
      throw new Error(error.error || error.detail || 'Failed to create business');
    }

    return response.json();
  }

  // PUT - Update existing business metadata and locations (no ID needed - updates current user's business)
  async updateBusiness(data: BusinessData): Promise<Business> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/business-admin/business/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update business' }));
      throw new Error(error.error || error.detail || 'Failed to update business');
    }

    return response.json();
  }

  // DELETE - Delete business (no ID needed - deletes current user's business)
  async deleteBusiness(): Promise<void> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/business-admin/business/`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete business' }));
      throw new Error(error.error || error.detail || 'Failed to delete business');
    }
  }

  // ============ Offerings APIs ============

  // GET - List all offerings for the business
  async getOfferings(): Promise<Offering[]> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/business-admin/offerings/`, {
      method: 'GET',
    });

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      throw new Error('Failed to fetch offerings');
    }

    const data = await response.json();
    // Handle various response formats
    if (Array.isArray(data)) {
      return data;
    }
    // If it's wrapped in a results/data/offerings key
    if (data.results && Array.isArray(data.results)) {
      return data.results;
    }
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    if (data.offerings && Array.isArray(data.offerings)) {
      return data.offerings;
    }
    // If single object, wrap in array
    if (data && typeof data === 'object' && data.id) {
      return [data];
    }
    return [];
  }

  // POST - Create a new offering
  async createOffering(data: OfferingData): Promise<Offering> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/business-admin/offerings/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create offering' }));
      throw new Error(error.error || error.detail || 'Failed to create offering');
    }

    return response.json();
  }

  // PUT - Update an existing offering
  async updateOffering(id: number, data: OfferingData): Promise<Offering> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/business-admin/offerings/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update offering' }));
      throw new Error(error.error || error.detail || 'Failed to update offering');
    }

    return response.json();
  }

  // DELETE - Delete an offering
  async deleteOffering(id: number): Promise<void> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/business-admin/offerings/${id}/`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete offering' }));
      throw new Error(error.error || error.detail || 'Failed to delete offering');
    }
  }

  // ============ Business Users APIs ============

  // GET - Fetch all users registered with the business
  async getBusinessUsers(): Promise<BusinessUsersResponse> {
    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/api/business-admin/business-users/`,
      { method: 'GET' }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return { users: [] };
      }
      throw new Error('Failed to fetch business users');
    }

    return response.json();
  }

  // ============ Redemptions APIs ============

  // GET - Fetch paginated list of redemptions
  async getRedemptions(page: number = 1, pageSize: number = 20): Promise<RedemptionsResponse> {
    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/api/business-admin/redemptions/?page=${page}&page_size=${pageSize}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return { items: [], total: 0, page: 1, page_size: pageSize };
      }
      throw new Error('Failed to fetch redemptions');
    }

    return response.json();
  }

  // ============ Super Admin APIs ============

  // GET - List all offerings (super admin)
  async getSuperAdminOfferings(): Promise<SuperAdminOffering[]> {
    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/api/super-admin/offerings/`,
      { method: 'GET' }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      throw new Error('Failed to fetch offerings');
    }

    const data = await response.json();
    if (Array.isArray(data)) {
      return data;
    }
    if (data.results && Array.isArray(data.results)) {
      return data.results;
    }
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    if (data.offerings && Array.isArray(data.offerings)) {
      return data.offerings;
    }
    return [];
  }

  // PUT - Update an offering (super admin endpoint)
  async updateSuperAdminOffering(id: number, data: SuperAdminOfferingUpdate): Promise<SuperAdminOffering> {
    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/api/super-admin/offerings/${id}/`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update offering' }));
      throw new Error(error.error || error.detail || 'Failed to update offering');
    }

    return response.json();
  }

  // ============ Hot Deals APIs (super admin) ============

  // GET - Current hot deals, ordered by hot_sort_order
  async getHotDeals(): Promise<SuperAdminOffering[]> {
    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/api/super-admin/hot-deals/`,
      { method: 'GET' }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      throw new Error('Failed to fetch hot deals');
    }

    const data = await response.json();
    if (Array.isArray(data)) {
      return data;
    }
    if (data.offerings && Array.isArray(data.offerings)) {
      return data.offerings;
    }
    if (data.results && Array.isArray(data.results)) {
      return data.results;
    }
    return [];
  }

  // PUT - Replace the hot deals carousel (full replace; omitted items removed)
  async setHotDeals(items: HotDealItem[]): Promise<SuperAdminOffering[]> {
    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/api/super-admin/hot-deals/`,
      {
        method: 'PUT',
        body: JSON.stringify({ items }),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update hot deals' }));
      throw new Error(error.error || error.details?.message || error.detail || 'Failed to update hot deals');
    }

    const data = await response.json();
    if (Array.isArray(data)) {
      return data;
    }
    if (data.offerings && Array.isArray(data.offerings)) {
      return data.offerings;
    }
    return [];
  }

  // DELETE - Delete a business (super admin only)
  async deleteSuperAdminBusiness(businessId: number): Promise<void> {
    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/api/super-admin/businesses/${businessId}/`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete business' }));
      throw new Error(error.error || error.detail || 'Failed to delete business');
    }
  }

  // GET - Public list of businesses (id + name only, cached on backend)
  async getPublicBusinesses(): Promise<PublicBusiness[]> {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/api/businesses/`, {
      method: 'GET',
    });

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      throw new Error('Failed to fetch businesses');
    }

    const data = await response.json();
    if (Array.isArray(data)) {
      return data;
    }
    if (data.businesses && Array.isArray(data.businesses)) {
      return data.businesses;
    }
    if (data.results && Array.isArray(data.results)) {
      return data.results;
    }
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  }

  // GET - List all redemptions (super admin) with filtering + pagination
  async getSuperAdminRedemptions(
    filters: SuperAdminRedemptionFilters = {}
  ): Promise<SuperAdminRedemptionsResponse> {
    const params = new URLSearchParams();
    const page = filters.page ?? 1;
    const pageSize = filters.page_size ?? 20;
    params.set('page', String(page));
    params.set('page_size', String(pageSize));
    if (filters.business_id != null) params.set('business_id', String(filters.business_id));
    if (filters.offering_id != null) params.set('offering_id', String(filters.offering_id));
    if (filters.user_id != null) params.set('user_id', String(filters.user_id));
    if (filters.user_email) params.set('user_email', filters.user_email);
    if (filters.redeemed_after) params.set('redeemed_after', filters.redeemed_after);
    if (filters.redeemed_before) params.set('redeemed_before', filters.redeemed_before);

    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/api/super-admin/redemptions/?${params.toString()}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return { items: [], total: 0, page, page_size: pageSize };
      }
      const error = await response.json().catch(() => ({ error: 'Failed to fetch redemptions' }));
      throw new Error(error.error || error.detail || 'Failed to fetch redemptions');
    }

    const data = await response.json();

    // Accept multiple response shapes defensively
    let items: SuperAdminRedemptionItem[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (Array.isArray(data.items)) {
      items = data.items;
    } else if (Array.isArray(data.results)) {
      items = data.results;
    } else if (Array.isArray(data.redemptions)) {
      items = data.redemptions;
    } else if (Array.isArray(data.data)) {
      items = data.data;
    }

    return {
      items,
      total: typeof data.total === 'number' ? data.total : (typeof data.count === 'number' ? data.count : items.length),
      page: typeof data.page === 'number' ? data.page : page,
      page_size: typeof data.page_size === 'number' ? data.page_size : pageSize,
    };
  }
}

// Business Users types
export interface BusinessUser {
  username: string;
  first_name: string;
  last_name: string;
  date_joined: string;
}

export interface BusinessUsersResponse {
  users: BusinessUser[];
}

// Redemption types
export interface RedemptionItem {
  username: string;
  redeemed_at: string;
  offering_name: string;
}

export interface RedemptionsResponse {
  items: RedemptionItem[];
  total: number;
  page: number;
  page_size: number;
}

// LinkPerks Business interface (from mobile app endpoint)
export interface LinkPerksBusinessResponse {
  id: number;
  name: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  category?: string;
  image?: string;
  cover_image?: string;
  phone?: string;
  email?: string;
  website?: string;
  offers_count?: number;
  featured?: boolean;
  is_active?: boolean;
  distance?: number;
  locations?: any[];
  created_at?: string;
}

// Super Admin Offering interface (includes business info)
export interface SuperAdminOffering {
  id: number;
  name: string;
  description: string;
  image?: string;
  type: string;
  code?: string;
  expiration_date?: string;
  amount?: number;
  distance?: number;
  is_active?: boolean;
  is_ecom?: boolean;
  redemption_mode?: RedemptionMode;
  website_url?: string | null;
  is_single_use?: boolean; // When true, each user can redeem only once. Defaults to unlimited.
  created_at?: string;
  updated_at?: string;
  business_id?: number;
  business_name?: string;
  location_ids?: number[];
}

// Super Admin update offering data
export interface SuperAdminOfferingUpdate {
  name: string;
  description: string;
  image?: string;
  type: string;
  code?: string;
  expiration_date?: string;
  amount?: number;
  distance?: number;
  is_active?: boolean;
  redemption_mode?: RedemptionMode;
  website_url?: string | null;
  is_single_use?: boolean; // When true, each user can redeem only once. Defaults to unlimited.
  location_ids?: number[];
}

// Public business (lightweight)
export interface PublicBusiness {
  id: number;
  name: string;
  email_domain?: string | null;
}

// Hot deals (super admin management)
export interface HotDealItem {
  offering_id: number;
  hot_sort_order: number;
}

// Filters for the super-admin redemptions endpoint
export interface SuperAdminRedemptionFilters {
  page?: number;
  page_size?: number;
  business_id?: number | null;
  offering_id?: number | null;
  user_id?: number | null;
  user_email?: string | null;
  // ISO 8601 datetime strings
  redeemed_after?: string | null;
  redeemed_before?: string | null;
}

// Item returned by /api/super-admin/redemptions/.
// Fields are optional because the backend may evolve; the UI parses defensively.
export interface SuperAdminRedemptionItem {
  id?: number;
  redeemed_at: string;

  user_id?: number;
  user_email?: string;
  username?: string;
  user_name?: string;
  first_name?: string;
  last_name?: string;

  business_id?: number;
  business_name?: string;

  offering_id?: number;
  offering_name?: string;
  offering_type?: string;
}

export interface SuperAdminRedemptionsResponse {
  items: SuperAdminRedemptionItem[];
  total: number;
  page: number;
  page_size: number;
}

export const apiService = new ApiService();

